import { useQueries, useQuery } from '@tanstack/react-query';
import {
  bankReconciliationDraftPath,
  bankReconciliationsPath,
  cashCountsPath,
  cashDayBookPath,
  dueExpensesPath,
  recurringExpensesPath,
  cashLedgerPath,
  cashInTransitPath,
  cashPositionPath,
  cashTransfersPath,
  cashVouchersPath,
  expenseAccountsPath,
  expenseSummaryPath,
  expensesPath,
} from '@core/api/queryPaths';
import { apiFetch } from './client';
import type { Paged } from './masters';
import type { CashAccountKind } from './generated/enums';

// The types live in the shared core (docs/Mobile_Apps_Design.md); re-exported so imports stay put.
export type * from '@core/api/cash';
// The writes both clients send moved with them (2026-10-14).
export {
  createCashTransfer,
  createCashVoucher,
  createCashVouchersInParts,
  createExpense,
  createExpensesInParts,
  receiveCashTransfer,
  reconcileBank,
  recordCashCount,
  saveRecurringExpense,
  sendCashToBranch,
  skipRecurringExpense,
  undoBankReconciliation,
  undoRecurringSkip,
  voidCashTransfer,
  voidCashVoucher,
  voidSentCashTransfer,
} from '@core/api/cashCommands';
import type {
  BankReconciliation,
  BankReconciliationDraft,
  CashDayBook,
  DueExpense,
  RecurringExpense,
  CashAccountBalance,
  CashAccountChoice,
  CashAccountInput,
  CashCount,
  CashInTransit,
  CashLedgerRow,
  CashPosition,
  CashTransfer,
  CashVoucher,
  Expense,
  ExpenseHead,
  ExpenseSummary,
} from '@core/api/cash';
export const cashKey = ['cash'] as const;
export const expensesKey = ['expense'] as const;

export function useCashPosition(branchId: number | undefined, from?: string, to?: string) {
  return useQuery({
    // The acting branch is part of the key for the reason every other register's is: without it,
    // switching branches keeps showing the previous shop's drawer — which here would be a figure
    // somebody counts money against.
    queryKey: [...cashKey, 'position', { branchId, from, to }],
    queryFn: () => apiFetch<CashPosition>(cashPositionPath(from, to)),
    enabled: branchId !== undefined,
  });
}

export function useCashLedger(
  branchId: number | undefined,
  filters: {
    accountId?: number | undefined;
    from?: string | undefined;
    to?: string | undefined;
    page?: number | undefined;
  } = {},
) {
  const { accountId, from, to, page = 1 } = filters;

  return useQuery({
    queryKey: [...cashKey, 'ledger', { branchId, accountId, from, to, page }],
    queryFn: () => apiFetch<Paged<CashLedgerRow>>(cashLedgerPath({ accountId, from, to, page })),
    enabled: branchId !== undefined,
  });
}

/**
 * Opens another account of a kind.
 *
 * <p>
 * This is what makes retiring one possible, which is why the two arrived together: while a branch
 * held exactly one till — created on demand and always the default — a retire flag was a column
 * nothing could set.
 * </p>
 */
export function createCashAccount(
  input: CashAccountInput & { kind: CashAccountKind; makeDefault?: boolean },
) {
  return apiFetch<CashAccountBalance>('/api/cash/accounts', { method: 'POST', body: input });
}

/**
 * Edits an account. The kind is fixed at creation and there is no delete — retiring is the
 * mechanism, and a retired account stays in the position while it holds anything.
 */
export function updateCashAccount(id: number, input: CashAccountInput & { isActive: boolean }) {
  return apiFetch<CashAccountBalance>(`/api/cash/accounts/${id}`, { method: 'PUT', body: input });
}

export function setDefaultCashAccount(id: number) {
  return apiFetch<CashAccountBalance>(`/api/cash/accounts/${id}/default`, { method: 'POST' });
}

export function setOpeningBalance(id: number, input: { amount: number; asOf: string }) {
  return apiFetch<CashAccountBalance>(`/api/cash/accounts/${id}/opening-balance`, {
    method: 'POST',
    body: input,
  });
}

export function useCashCounts(
  branchId: number | undefined,
  filters: {
    accountId?: number | undefined;
    from?: string | undefined;
    to?: string | undefined;
    page?: number | undefined;
  } = {},
) {
  const { accountId, from, to, page = 1 } = filters;

  return useQuery({
    queryKey: [...cashKey, 'counts', { branchId, accountId, from, to, page }],
    queryFn: () => apiFetch<Paged<CashCount>>(cashCountsPath({ accountId, from, to, page })),
    enabled: branchId !== undefined,
  });
}

/** The recent moves between the branch's accounts — the cash book's "Money moved" card. */
export function useCashTransfers(branchId: number | undefined) {
  return useQuery({
    queryKey: [...cashKey, 'transfers', { branchId }],
    queryFn: () => apiFetch<Paged<CashTransfer>>(cashTransfersPath()),
    enabled: branchId !== undefined,
  });
}

/** Money between branches, outgoing and incoming — the cash book's amber notices (step 5). */
export function useCashInTransit(branchId: number | undefined) {
  return useQuery({
    queryKey: [...cashKey, 'in-transit', { branchId }],
    queryFn: () => apiFetch<CashInTransit>(cashInTransitPath()),
    enabled: branchId !== undefined,
  });
}

/** Owner's money, drawings and other income — the cash book's "Money in and out" card. */
export function useCashVouchers(branchId: number | undefined) {
  return useQuery({
    queryKey: [...cashKey, 'vouchers', { branchId }],
    queryFn: () => apiFetch<Paged<CashVoucher>>(cashVouchersPath()),
    enabled: branchId !== undefined,
  });
}

/**
 * What a bank's reconcile page starts from, as at the statement day typed. Disabled until a day is
 * given, since the book figure is the close of that day.
 */
export function useBankReconciliationDraft(
  branchId: number | undefined,
  accountId: number | undefined,
  statementDate: string,
) {
  return useQuery({
    queryKey: [...cashKey, 'reconciliation-draft', { branchId, accountId, statementDate }],
    queryFn: () =>
      apiFetch<BankReconciliationDraft>(bankReconciliationDraftPath(accountId ?? 0, statementDate)),
    enabled: branchId !== undefined && accountId !== undefined && statementDate !== '',
  });
}

/**
 * The day book for one day, one read per account (step 8) — in the order asked, `null` for any still
 * loading, so a print waits for all of them rather than going out with an account missing.
 */
export function useCashDayBooks(
  branchId: number | undefined,
  accountIds: readonly number[],
  date: string,
): { books: CashDayBook[] | null; error: unknown } {
  const results = useQueries({
    queries: accountIds.map((accountId) => ({
      queryKey: [...cashKey, 'day-book', { branchId, accountId, date }],
      queryFn: () => apiFetch<CashDayBook>(cashDayBookPath(accountId, date)),
      enabled: branchId !== undefined && date !== '',
    })),
  });

  const error = results.find((result) => result.error)?.error ?? null;
  const books = results.every((result) => result.data)
    ? results.map((result) => result.data as CashDayBook)
    : null;

  return { books, error };
}

/** The statements already matched — the cash book's "Bank statements" card (step 7). */
export function useBankReconciliations(branchId: number | undefined) {
  return useQuery({
    queryKey: [...cashKey, 'reconciliations', { branchId }],
    queryFn: () => apiFetch<Paged<BankReconciliation>>(bankReconciliationsPath()),
    enabled: branchId !== undefined,
  });
}

/**
 * What a voucher can be paid from. Under the cash key, so adding or retiring an account on Cash &
 * bank reaches the picker without a reload.
 */
export function useExpenseAccounts(branchId: number | undefined) {
  return useQuery({
    queryKey: [...cashKey, 'paid-from', 'expense', { branchId }],
    queryFn: () => apiFetch<CashAccountChoice[]>(expenseAccountsPath()),
    enabled: branchId !== undefined,
  });
}

/** The branch's recurring expenses (step 8). Under the expense key, so recording a voucher refreshes "next due". */
export function useRecurringExpenses(branchId: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...expensesKey, 'recurring', { branchId, includeInactive }],
    queryFn: () => apiFetch<RecurringExpense[]>(recurringExpensesPath(includeInactive)),
    enabled: branchId !== undefined,
  });
}

/** What is due, overdue or coming up within the week — listed, never posted. */
export function useDueExpenses(branchId: number | undefined) {
  return useQuery({
    queryKey: [...expensesKey, 'recurring', 'due', { branchId }],
    queryFn: () => apiFetch<DueExpense[]>(dueExpensesPath()),
    enabled: branchId !== undefined,
  });
}

export function useExpenseHeads(branchId: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...expensesKey, 'heads', { branchId, includeInactive }],
    queryFn: () =>
      apiFetch<ExpenseHead[]>(
        `/api/expense/heads${includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: branchId !== undefined,
  });
}

export function useExpenses(
  branchId: number | undefined,
  filters: {
    from?: string | undefined;
    to?: string | undefined;
    headId?: number | undefined;
    includeVoided?: boolean | undefined;
    search?: string | undefined;
    page?: number | undefined;
    /**
     * How many rows to fetch, where the default is a register page.
     *
     * Settable so a caller that only reads `total` — the morning screen's voucher counts — can ask
     * for one row instead of twenty-five it will throw away. The count itself is the server's,
     * never the page's length.
     */
    pageSize?: number | undefined;
  } = {},
) {
  const { from, to, headId, includeVoided = false, search, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [
      ...expensesKey,
      { branchId, from, to, headId, includeVoided, search, page, pageSize },
    ],
    queryFn: () => apiFetch<Paged<Expense>>(expensesPath(filters)),
    enabled: branchId !== undefined,
  });
}

/** What the branch spent between two days, by head and by account — the Expenses board's figures. */
export function useExpenseSummary(
  branchId: number | undefined,
  from: string,
  to: string,
  enabled = true,
) {
  return useQuery({
    queryKey: [...expensesKey, 'summary', { branchId, from, to }],
    queryFn: () => apiFetch<ExpenseSummary>(expenseSummaryPath(from, to)),
    enabled: enabled && branchId !== undefined && from !== '' && to !== '',
  });
}

/**
 * Calls a voucher off.
 *
 * <p>
 * There is deliberately no update call beside this: a voucher says money left the drawer on a day,
 * and editing one rewrites what the cash book already reported. The number stays in the sequence
 * and the server posts a reversing ledger row dated today.
 * </p>
 */
export function voidExpense(id: number, reason: string) {
  return apiFetch<Expense>(`/api/expense/${id}/void`, { method: 'POST', body: { reason } });
}

export function createExpenseHead(input: { code: string; name: string; shared?: boolean }) {
  return apiFetch<ExpenseHead>('/api/expense/heads', { method: 'POST', body: input });
}

export function updateExpenseHead(
  id: number,
  input: { code: string; name: string; isActive: boolean },
) {
  return apiFetch<ExpenseHead>(`/api/expense/heads/${id}`, { method: 'PUT', body: input });
}

export function deleteExpenseHead(id: number) {
  return apiFetch<void>(`/api/expense/heads/${id}`, { method: 'DELETE' });
}
